import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DraftNotice } from '../../components/DraftNotice';
import { Modal } from '../../components/Modal';
import { OptionChips } from '../../components/OptionChips';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { clearDraft, restoreDraft } from '../../lib/draftStorage';
import {
  PLATFORM_OPTIONS,
  TEXT_ONLY_PLATFORMS,
  TEXT_ONLY_UNAVAILABLE_REASON,
  postTypeAllowedOn,
} from '../../lib/platforms';
import { domainOf, safeExternalUrl } from '../../lib/url';
import { useDraft } from '../../lib/useDraft';
import type {
  CampaignRead,
  PlanAutomation,
  PlannedPostCreate,
  PlannedPostRead,
  PlatformName,
  PostType,
} from '../../types/api';
import { IntentPicker } from '../generation/IntentPicker';
import { knownIntents } from '../generation/intents';
import { useIntents } from '../generation/useIntents';
import { useConnections } from '../knowledge-base/useConnections';
import {
  AUTOMATION_HINTS,
  AUTOMATION_OPTIONS,
  DEFAULT_PUBLISH_TIME,
  describeAutomation,
  isClaimed,
  isResettable,
} from './automation';
import { dayLabel, localTimeOf, toInstant } from './calendarDates';
import { plannedPostDraftKey } from './calendarDrafts';

const TITLE_MAX_LENGTH = 200;

/** Post types, worded as on the generation form so the states match. */
const POST_TYPE_OPTIONS: readonly { value: PostType; label: string }[] = [
  { value: 'single_image', label: 'Single image' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'text_only', label: 'Text only' },
];

/** Where a LinkedIn account gets connected. */
const INTEGRATIONS_PATH = '/knowledge-base?section=integrations';

/** Autosaved shape. Field names are the form's, not the API's. */
interface PlannedPostDraft {
  title: string;
  brief: string;
  scheduledOn: string;
  platform: string;
  postType: string;
  /** Registry keys in pick order; narrowed against the registry on the way out. */
  intents: string[];
  campaignId: string;
  automation: string;
  publishTime: string;
}

/** A restored draft is untrusted input; a platform we do not offer is dropped. */
function safePlatform(candidate: string): PlatformName | null {
  return PLATFORM_OPTIONS.find((option) => option.value === candidate)?.value ?? null;
}

function safePostType(candidate: string): PostType | null {
  return POST_TYPE_OPTIONS.find((option) => option.value === candidate)?.value ?? null;
}

function safeAutomation(candidate: string): PlanAutomation {
  return AUTOMATION_OPTIONS.find((option) => option.value === candidate)?.value ?? 'none';
}

/**
 * What a new post opens with when it was started somewhere other than the
 * calendar — today, only the industry newsfeed.
 *
 * A prefill also switches the autosaved draft off: the stored draft belongs to
 * the calendar's own quick-add form, and restoring it over an article the user
 * just clicked would replace what they asked for with something else.
 */
export interface PlannedPostPrefill {
  title: string;
  brief: string;
  /** Carried through the submit untouched. There is no field for it. */
  source_url: string;
}

interface PlannedPostModalProps {
  businessId: string;
  /** The post being edited, or null for a new one. */
  initial: PlannedPostRead | null;
  /** Date a new post opens on — the day that was clicked. */
  defaultDate: string;
  /** Create mode only: values carried in from another screen. */
  prefill?: PlannedPostPrefill | null;
  campaigns: readonly CampaignRead[];
  isSaving: boolean;
  error: string | null;
  /** The save's 402, when the plan refused it; drawn as the Upsell. */
  planError?: unknown;
  onSubmit: (body: PlannedPostCreate) => void;
  /** Edit mode only. Deletes behind the six-second undo window. */
  onDelete?: (post: PlannedPostRead) => void;
  /** Edit mode, parked or failed plan only: hand it back to the scheduler. */
  onReset?: (post: PlannedPostRead) => void;
  /** Edit mode, idle automated plan only: start it now rather than at its slot. */
  onRunNow?: (post: PlannedPostRead) => void;
  /** Escape or Close: leaves any draft where it is. */
  onDismiss: () => void;
}

/**
 * Plan one post: what it is, when, and how it goes out.
 *
 * Title is the only thing it insists on for a plain plan. Platform and post
 * type are the brief the user means to submit later and can stay open until
 * they decide, so both chip groups deselect on a second click. The campaign
 * select is absent entirely until at least one campaign exists.
 *
 * An *automated* plan (generate for review, or post automatically) is the one
 * thing on the calendar that does spend a generation, later, without the user
 * present — so it insists on more: a platform, a post type and a time. Posting
 * automatically is LinkedIn-only and needs a live connection, checked here so
 * the person who can fix that is told while they are at the keyboard; the
 * server checks the same things again.
 *
 * Once the worker holds a plan its slot is frozen (the server answers 409 for
 * any change to it), so the form shows those fields read-only with the plan's
 * state, and offers a reset where one is legal. Title, brief and campaign
 * stay editable throughout.
 *
 * A post planned off a news article carries the article's URL. It is stated as
 * a read-only source line and travels back out on submit — there is no field
 * for it, because it is a record of where the idea came from, not a decision.
 */
export function PlannedPostModal({
  businessId,
  initial,
  defaultDate,
  prefill = null,
  campaigns,
  isSaving,
  error,
  planError = null,
  onSubmit,
  onDelete,
  onReset,
  onRunNow,
  onDismiss,
}: PlannedPostModalProps) {
  // A prefilled form is about one specific article, so it neither restores nor
  // writes the calendar's shared "new post" draft.
  const draftKey = prefill === null ? plannedPostDraftKey(businessId, initial) : null;
  /** Where this post came from: the article behind it, however it was opened. */
  const sourceUrl = prefill?.source_url ?? initial?.source_url ?? null;
  const sourceHref = safeExternalUrl(sourceUrl);
  /** The worker holds this plan: its slot cannot change until a reset. */
  const claimed = initial !== null && isClaimed(initial);
  const pristine: PlannedPostDraft = {
    title: prefill?.title ?? initial?.title ?? '',
    brief: prefill?.brief ?? initial?.brief ?? '',
    scheduledOn: initial?.scheduled_on ?? defaultDate,
    platform: initial?.platform ?? '',
    postType: initial?.post_type ?? '',
    intents: initial?.intents ?? [],
    campaignId: initial?.campaign_id ?? '',
    automation: initial?.automation ?? 'none',
    publishTime:
      (initial?.publish_at ? localTimeOf(initial.publish_at) : null) ?? DEFAULT_PUBLISH_TIME,
  };
  // The form mounts when the modal opens, so this initialiser is the "on open"
  // hook — no effect, no second render.
  const [restored] = useState(() => restoreDraft(draftKey, pristine));
  const [title, setTitle] = useState(restored.value.title);
  const [brief, setBrief] = useState(restored.value.brief);
  const [scheduledOn, setScheduledOn] = useState(restored.value.scheduledOn);
  const [platform, setPlatform] = useState<PlatformName | null>(
    safePlatform(restored.value.platform),
  );
  const [postType, setPostType] = useState<PostType | null>(safePostType(restored.value.postType));
  const [intents, setIntents] = useState<string[]>(restored.value.intents);
  const [campaignId, setCampaignId] = useState(restored.value.campaignId);
  const [automation, setAutomation] = useState<PlanAutomation>(
    safeAutomation(restored.value.automation),
  );
  const [publishTime, setPublishTime] = useState(restored.value.publishTime);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Read whenever the form is open: cheap, and it lets the auto option say
  // "connect LinkedIn first" before the user picks it rather than after.
  const connections = useConnections(businessId);
  const linkedin = connections.linkedin;
  // Same rule as the brief's: a purpose the registry no longer carries is
  // dropped rather than saved onto a plan the worker would then run with.
  const { intents: intentRegistry } = useIntents();
  const selectedIntents = knownIntents(intents, intentRegistry);
  const canPostUnattended =
    linkedin !== null && linkedin.available && linkedin.status === 'connected';
  // Posting with nobody watching is Autopilot. Review mode stays open on every plan.
  const { can } = usePlan();
  const canAuto = can('autopilot');

  useDraft(
    draftKey,
    {
      title,
      brief,
      scheduledOn,
      platform: platform ?? '',
      postType: postType ?? '',
      intents,
      campaignId,
      automation,
      publishTime,
    },
    pristine,
  );

  const automated = automation !== 'none';

  function chooseAutomation(value: string) {
    const next = safeAutomation(value);
    setAutomation(next);
    // Unattended posting is LinkedIn-only: choosing it also chooses the platform.
    if (next === 'auto') setPlatform('linkedin');
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!title.trim()) {
      setFieldError('Name the post before saving.');
      return;
    }
    // A cleared date field falls back to the day the form opened on rather
    // than blocking a save on the one field the user did not come here for.
    const day = scheduledOn || defaultDate;
    let publishAt: string | null = null;

    // Instagram and TikTok take no text-only post; the server refuses the pair.
    if (
      !claimed &&
      platform !== null &&
      postType !== null &&
      !postTypeAllowedOn(postType, platform)
    ) {
      setFieldError(TEXT_ONLY_UNAVAILABLE_REASON);
      return;
    }

    if (automated && !claimed) {
      if (platform === null || postType === null) {
        setFieldError('An automated post needs a platform and a post type.');
        return;
      }
      if (automation === 'auto' && !canAuto) {
        setFieldError('Posting automatically needs a plan with Autopilot.');
        return;
      }
      if (automation === 'auto' && platform !== 'linkedin') {
        setFieldError('Automatic posting is only available for LinkedIn.');
        return;
      }
      if (automation === 'auto' && !canPostUnattended) {
        setFieldError('Connect a LinkedIn account before scheduling an automatic post.');
        return;
      }
      publishAt = toInstant(day, publishTime);
      if (publishAt === null) {
        setFieldError('Set a time for the post to go out.');
        return;
      }
      if (new Date(publishAt).getTime() < Date.now() - 5 * 60_000) {
        setFieldError('That time has already passed. Pick a later one.');
        return;
      }
    }

    setFieldError(null);
    onSubmit({
      title: title.trim(),
      brief: brief.trim() || null,
      scheduled_on: day,
      platform,
      post_type: postType,
      // Frozen once the worker holds the plan, like the rest of the slot — the
      // caller drops it from the payload rather than provoking a 409.
      intents: selectedIntents,
      campaign_id: campaignId || null,
      // Provenance, not input: sent back exactly as it arrived so an edit
      // cannot detach a post from the article it was planned off.
      source_url: sourceUrl,
      automation,
      publish_at: claimed ? (initial?.publish_at ?? null) : publishAt,
    });
  }

  /** Cancel throws the work away. Escape and Close do not. */
  function handleCancel() {
    clearDraft(draftKey);
    onDismiss();
  }

  const message = fieldError ?? error;
  const state = initial === null ? null : describeAutomation(initial);

  return (
    <Modal
      title={
        initial === null ? 'New planned post' : `Planned post · ${dayLabel(initial.scheduled_on)}`
      }
      onDismiss={onDismiss}
    >
      <form className="form" onSubmit={handleSubmit} noValidate>
        {restoredAt ? (
          <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
        ) : null}

        {/* Provenance, stated once and not editable — the title and the brief
            below are the user's to change, where the post came from is not. */}
        {sourceUrl ? (
          <p className="post-source">
            <span className="post-source__label">Source</span>
            {sourceHref ? (
              <a
                className="post-source__link"
                href={sourceHref}
                target="_blank"
                rel="noreferrer noopener"
              >
                {domainOf(sourceHref) ?? sourceHref}
              </a>
            ) : (
              <span className="post-source__link">{sourceUrl}</span>
            )}
          </p>
        ) : null}

        {/* The worker holds this plan. Say where it is, offer the way back. */}
        {initial !== null && claimed ? (
          <div className="plan-state" role="status">
            <p className="plan-state__text">{state}</p>
            <div className="plan-state__actions">
              {initial.generation_request_id ? (
                <Link
                  className="button button--ghost"
                  to={`/review/${initial.generation_request_id}`}
                >
                  Open review
                </Link>
              ) : null}
              {onReset !== undefined && isResettable(initial) ? (
                <button
                  className="button button--ghost"
                  type="button"
                  disabled={isSaving}
                  onClick={() => onReset(initial)}
                >
                  Reset and try again
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="kb-form__row">
          <label className="field">
            <span className="field__label">Title</span>
            <input
              className="field__input"
              type="text"
              maxLength={TITLE_MAX_LENGTH}
              placeholder="Spring blend teaser"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Date</span>
            <input
              className="field__input"
              type="date"
              value={scheduledOn}
              disabled={claimed}
              onChange={(event) => setScheduledOn(event.target.value)}
            />
            {/* A finger cannot drag a chip to another day; this is the move.
                Shown only where nothing hovers (see .planned-post__move-hint). */}
            {initial && !claimed ? (
              <span className="field__hint planned-post__move-hint">
                Change the date to move this post
              </span>
            ) : null}
          </label>
        </div>

        <OptionChips
          legend="How it goes out"
          hint={AUTOMATION_HINTS[automation]}
          options={AUTOMATION_OPTIONS}
          selected={[automation]}
          mode="single"
          name="automation"
          disabled={claimed}
          onToggle={chooseAutomation}
          unavailable={canAuto || claimed ? [] : ['auto']}
        />
        {canAuto || claimed ? null : <Upsell feature="autopilot" />}

        {automated ? (
          <div className="kb-form__row">
            <label className="field">
              <span className="field__label">Time</span>
              <input
                className="field__input"
                type="time"
                value={publishTime}
                disabled={claimed}
                onChange={(event) => setPublishTime(event.target.value)}
              />
              <span className="field__hint">
                {automation === 'review'
                  ? 'The draft is ready a day before this.'
                  : 'Your local time. The post lands within a few minutes of it.'}
              </span>
            </label>
          </div>
        ) : null}

        {automation === 'auto' && !claimed && !connections.isLoading && !canPostUnattended ? (
          <p className="form__notice">
            {linkedin !== null && !linkedin.available
              ? 'LinkedIn publishing is not configured on this deployment.'
              : linkedin?.status === 'expired'
                ? 'Your LinkedIn connection has expired. '
                : 'No LinkedIn account is connected yet. '}
            {linkedin === null || linkedin.available ? (
              <Link to={INTEGRATIONS_PATH}>
                {linkedin?.status === 'expired' ? 'Reconnect it' : 'Connect one'} in Connected
                accounts.
              </Link>
            ) : null}
          </p>
        ) : null}

        <OptionChips
          legend="Platform"
          hint={
            automation === 'auto'
              ? 'Automatic posting goes to LinkedIn.'
              : automated
                ? 'Required for a post we create for you.'
                : 'Optional. Leave it open until you have decided.'
          }
          options={
            automation === 'auto'
              ? PLATFORM_OPTIONS.filter((o) => o.value === 'linkedin')
              : PLATFORM_OPTIONS
          }
          selected={platform === null ? [] : [platform]}
          disabled={claimed}
          onToggle={(value) => {
            const next = platform === value ? null : safePlatform(value);
            setPlatform(next);
            // Instagram and TikTok take no text-only post: fall back to a single image.
            if (postType === 'text_only' && next !== null && !TEXT_ONLY_PLATFORMS.includes(next)) {
              setPostType('single_image');
            }
          }}
        />

        <OptionChips
          legend="Post type"
          hint={[
            automated ? 'Required for a post we create for you.' : 'Optional.',
            platform !== null && !TEXT_ONLY_PLATFORMS.includes(platform)
              ? TEXT_ONLY_UNAVAILABLE_REASON
              : null,
          ]
            .filter(Boolean)
            .join(' ')}
          options={POST_TYPE_OPTIONS}
          unavailable={
            platform !== null && !TEXT_ONLY_PLATFORMS.includes(platform) ? ['text_only'] : []
          }
          selected={postType === null ? [] : [postType]}
          disabled={claimed}
          onToggle={(value) =>
            setPostType((current) => (current === value ? null : safePostType(value)))
          }
        />

        <label className="field">
          <span className="field__label">Brief</span>
          <textarea
            className="field__input"
            rows={4}
            placeholder="What this post should say, and what it should push."
            value={brief}
            onChange={(event) => setBrief(event.target.value)}
          />
        </label>

        {/* Under the brief, because it is the same question one level up: the
            brief says what the post is about, this says what it is for. It
            rides to the pipeline whenever this plan is run, by hand or by the
            worker — and freezes with the rest of the slot once claimed. */}
        <IntentPicker
          legend="What is this post for?"
          hint={
            claimed
              ? 'Set when we started on this post.'
              : 'Optional. Up to two — the first is what the post leads with.'
          }
          value={selectedIntents}
          onChange={setIntents}
          disabled={claimed}
        />

        {/* Campaigns carry no weight on the screen until one exists. */}
        {campaigns.length > 0 ? (
          <label className="field">
            <span className="field__label">Campaign</span>
            <select
              className="field__input"
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
            >
              <option value="">No campaign</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {message ? (
          <p className="form__error" role="alert">
            {message}
          </p>
        ) : null}
        <UpsellFromError error={planError} />

        {/* An automated plan that has not started yet can be started now. */}
        {initial !== null && !claimed && initial.automation !== 'none' && onRunNow !== undefined ? (
          <p className="field__hint plan-run-now">
            Scheduled for its slot.{' '}
            <button
              className="button button--ghost button--small"
              type="button"
              disabled={isSaving}
              onClick={() => onRunNow(initial)}
            >
              Create post now
            </button>
          </p>
        ) : null}

        <div className="modal__actions">
          {initial === null || onDelete === undefined ? null : (
            <button
              className="button button--ghost button--danger-text modal__actions-lead"
              type="button"
              disabled={isSaving}
              onClick={() => onDelete(initial)}
            >
              Delete
            </button>
          )}
          <button className="button" type="button" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={isSaving}>
            {isSaving ? 'Saving' : 'Save post'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
